import { useContext, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import AddIcon from "../../../assets/add.svg?react";
import DataTableIcon from "../../../assets/datatable.svg?react";
import TrashCanIcon from "../../../assets/trash-can.svg?react";
import { AppConfig } from "../../../AppConfig";
import { RightPanelTabs } from "../../../constants/Panels";
import { AppContext, LighthouseContext, UserContext } from "../../../Context";
import { setLoading, setRightPanelActiveTab } from "../../../store/appSlice";
import {
    setCurrentLighthouseTrueNorth,
    setIsSavingLighthouse,
    setIsSectorEditTableOpen,
    setSectorOrder,
    setSectorSourceStep,
} from "../../../store/lighthouseSlice";
import { StoreState } from "../../../store/rootReducer";
import { LighthouseData, MAX_SECTOR_DESCRIPTION_LENGTH, SectorColor } from "../../../utils/lighthouseConstants";
import { createPendingLighthouse } from "../../../utils/pendingLighthouse";
import { FRA_DESCRIPTION_HINT, shiftSectors } from "../../../utils/sectorRules";
import { commitSectorTableEdit } from "../../../utils/sectorTableCommit";
import {
    convertDraftsToUnit,
    emptySectorDraft,
    insertedSectorDraft,
    LengthUnit,
    numericDraftFromRaw,
    SectorDraft,
    sectorDraftNumbers,
    sectorDraftsFromSectors,
    sectorDraftsToSectors,
    updateLinkedAngle,
    updateLinkedLineLength,
} from "../../../utils/sectorDraft";
import { hasBlockingErrors, validateSectorDrafts } from "../../../utils/sectorDraftValidation";
import ErrorMessage from "../../genericComponents/ErrorMessage/ErrorMessage";
import FloatingWindow from "../../genericComponents/FloatingWindow/FloatingWindow";
import InputField from "../../genericComponents/InputField/InputField";
import NfsButton from "../../genericComponents/NfsButton/NfsButton";
import NfsDropdown from "../../genericComponents/NfsDropdown/NfsDropdown";
import RadioButtonGroup from "../../genericComponents/RadioButton/RadioButtonGroup";
import SectorColorIndicator from "./SectorColorIndicator";
import "./SectorDefinitionTable.css";

const colorTranslations = { WHITE: "Hvit", RED: "Rød", GREEN: "Grønn", DARK: "Mørk" };
const units: LengthUnit[] = ["Nm", "m"];

/**
 * "create" is the step of the create flow where the user chose to define sectors in a table;
 * the lighthouse does not exist yet. "edit" reworks the sectors of a lighthouse the sector
 * editor already has loaded. Either way the rows are a draft: nothing is written until save.
 */
export type SectorDefinitionTableMode = "create" | "edit";

interface SectorDefinitionTableProps {
    mode: SectorDefinitionTableMode;
}

const SectorDefinitionTable = (props: SectorDefinitionTableProps) => {
    const isEditing = props.mode === "edit";
    const dispatch = useDispatch();
    const tableRef = useRef(document.createElement("div"));
    const appContext = useContext(AppContext);
    const lighthouseContext = useContext(LighthouseContext);
    const userContext = useContext(UserContext);

    const sectorSourceStep = useSelector((state: StoreState) => state.lighthouse.sectorSourceStep);
    const trueNorth = useSelector((state: StoreState) => state.lighthouse.currentLighthouseTrueNorth);
    const sectorOrder = useSelector((state: StoreState) => state.lighthouse.sectorOrder);
    const placement = appContext?.pendingLighthousePlacement.value.current;
    const editedLighthouse = lighthouseContext?.lighthouseData.value;
    const editedFeature = appContext?.selectedFeature.value;

    const [unitIndex, setUnitIndex] = useState(0);
    // Seeded once, on mount: the rows are the user's to edit from here on, and re-seeding them
    // from the lighthouse would throw away what they have typed. Both mounts are gated, so a
    // second visit to the table is a fresh component with a fresh seed. The one exception is a
    // save in edit mode, which re-seeds from what it saved (see save).
    const [drafts, setDrafts] = useState<SectorDraft[]>(() =>
        sectorDraftsFromSectors(
            isEditing ? (editedLighthouse?.sectors ?? []) : AppConfig.Lighthouse.DefaultSectors,
            "Nm",
            isEditing ? sectorOrder : null
        )
    );
    const [isCreating, setIsCreating] = useState(false);
    // Fixed at mount: it is only where the window opens, after that the user sizes it
    const [width] = useState(() => Math.min(1500, window.innerWidth - 40));

    const unit = units[unitIndex];
    const findings = useMemo(() => validateSectorDrafts(drafts), [drafts]);
    const sectorNumbers = sectorDraftNumbers(drafts);

    if (isEditing ? !editedLighthouse || !editedFeature : sectorSourceStep !== "table" || !placement) return null;

    const findingFor = (rowKey: string, field: keyof SectorDraft) =>
        findings.find((finding) => finding.rowKey === rowKey && finding.field === field)?.message;

    const tableFindings = findings.filter((finding) => !finding.rowKey);

    const updateDraft = (key: string, changes: Partial<SectorDraft>) =>
        setDrafts((current) => current.map((draft) => (draft.key === key ? { ...draft, ...changes } : draft)));

    /** Adds a row directly below `index`, seeded from the rows it lands between. The circle is
     *  cyclic, so a row added below the last one is seeded from the first. */
    const insertDraftBelow = (index: number) =>
        setDrafts((current) => {
            const inserted = insertedSectorDraft(current[index], current[(index + 1) % current.length], unit);
            return [...current.slice(0, index + 1), inserted, ...current.slice(index + 1)];
        });

    const switchUnit = (index: number) => {
        setUnitIndex(index);
        setDrafts((current) => convertDraftsToUnit(current, units[index]));
    };

    const close = () => {
        // Half a save is not a state the table can be closed out of
        if (isCreating) return;

        if (isEditing) {
            // The sector editor stays as it was: the table wrote nothing to it
            dispatch(setIsSectorEditTableOpen(false));
            return;
        }
        appContext!.pendingLighthousePlacement.value.current = null;
        appContext!.createObjectCurrentEntryRef.value.current = null;
        dispatch(setSectorSourceStep("none"));
    };

    const save = async () => {
        setIsCreating(true);

        if (isEditing) {
            let updated: LighthouseData;
            try {
                updated = await commitSectorTableEdit({
                    lighthouse: editedLighthouse!,
                    drafts,
                    feature: editedFeature!,
                    trueNorth,
                    setSelectedFeature: appContext?.selectedFeature.set,
                    setLighthouseData: (lighthouseData) => lighthouseContext?.lighthouseData.set(lighthouseData),
                    setIsSavingLighthouse: (isSaving) => dispatch(setIsSavingLighthouse(isSaving)),
                    setLoading: (loadingStatus) => dispatch(setLoading(loadingStatus)),
                });
            } catch (error) {
                console.error("Kunne ikke lagre sektorene", error);
                toast.error("Kunne ikke lagre sektorene. Verdiene i tabellen er beholdt.");
                // The save turns the loader on and only turns it off when it runs to the end,
                // so a failure has to clear it or the overlay hides the table it asks to retry
                dispatch(setLoading(false));
                dispatch(setIsSavingLighthouse(false));
                setIsCreating(false);
                return;
            }

            // The table stays open so the result can be checked in the map and the work carried
            // on. Its rows are re-seeded from what was saved: an added row only got its globalid
            // on the way in, and saving the old rows again would create that sector a second time.
            // A save is when the sector order and numbers may change, so take a new snapshot
            const updatedOrder = shiftSectors(updated).map((sector) => sector.globalid);
            dispatch(setSectorOrder(updatedOrder));
            setDrafts(sectorDraftsFromSectors(updated.sectors, unit, updatedOrder));
            setIsCreating(false);
            toast.success("Sektorene er lagret");
            return;
        }

        const entryTitle = appContext?.createObjectCurrentEntryRef.value.current;

        try {
            await createPendingLighthouse({
                placement: placement!,
                sectors: sectorDraftsToSectors(drafts),
                userName: userContext?.user.value?.fullName,
                entryTitle,
                setSelectedFeature: appContext?.selectedFeature.set,
                setLighthouseData: (lighthouseData) => lighthouseContext?.lighthouseData.set(lighthouseData),
                setTrueNorth: (trueNorth) => dispatch(setCurrentLighthouseTrueNorth(trueNorth)),
                setIsSavingLighthouse: (isSaving) => dispatch(setIsSavingLighthouse(isSaving)),
                setLoading: (loadingStatus) => dispatch(setLoading(loadingStatus)),
            });
        } catch (error) {
            console.error("Kunne ikke opprette fyrlykten", error);
            toast.error("Kunne ikke opprette fyrlykten. Verdiene i tabellen er beholdt.");
            dispatch(setLoading(false));
            dispatch(setIsSavingLighthouse(false));
            setIsCreating(false);
            return;
        }

        appContext!.pendingLighthousePlacement.value.current = null;
        appContext!.createObjectCurrentEntryRef.value.current = null;
        dispatch(setSectorSourceStep("none"));
        dispatch(setRightPanelActiveTab(RightPanelTabs.EDITOR));
    };

    const numberCell = (draft: SectorDraft, field: "fromAngle" | "toAngle") => (
        <td>
            <InputField
                inputFieldId={`${draft.key}-${field}`}
                label=""
                type="number"
                step="0.01"
                value={draft[field]}
                hasError={Boolean(findingFor(draft.key, field))}
                onChange={(value) => setDrafts((current) => updateLinkedAngle(current, draft.key, field, value))}
            />
            {findingFor(draft.key, field) ? <ErrorMessage message={findingFor(draft.key, field)!} light /> : null}
        </td>
    );

    /**
     * A dark sector is the absence of light: it has no radius, and the boundary lines around it
     * belong to the lit sectors on either side. Showing empty inputs would invite values that
     * are then thrown away, so the cells say plainly that they do not apply.
     */
    const lengthCell = (draft: SectorDraft, field: "fromLineLength" | "toLineLength" | "innerRadius") =>
        draft.color === SectorColor.DARK ? (
            <td className="sector-definition-not-applicable" title="Gjelder ikke mørke sektorer">
                –
            </td>
        ) : (
            <td>
                <InputField
                    inputFieldId={`${draft.key}-${field}`}
                    label=""
                    type="number"
                    step="0.01"
                    value={draft[field].raw}
                    hasError={Boolean(findingFor(draft.key, field))}
                    onChange={(value) => {
                        const length = numericDraftFromRaw(value, unit);
                        if (field === "innerRadius") {
                            updateDraft(draft.key, { innerRadius: length });
                        } else {
                            setDrafts((current) => updateLinkedLineLength(current, draft.key, field, length));
                        }
                    }}
                />
                {findingFor(draft.key, field) ? <ErrorMessage message={findingFor(draft.key, field)!} light /> : null}
            </td>
        );

    return (
        <FloatingWindow
            title={isEditing ? "Rediger sektorer" : "Definer sektorer"}
            className="sector-definition-window"
            contentClassName="sector-definition-window-content"
            toolRef={tableRef}
            closeWindow={close}
            Icon={DataTableIcon}
            width={width}
            defaultPosition={{ x: -width / 2, y: 0 }}
            busy={isCreating}
            resizable
            minimizable
        >
            <RadioButtonGroup options={["Vis i Nm", "Vis i m"]} selectedIndex={unitIndex} onChange={switchUnit} />
            <p className="sector-definition-hint">{FRA_DESCRIPTION_HINT}</p>

            <div className="sector-definition-scroll">
                <table className="sector-definition-table">
                    <thead>
                        <tr>
                            <th>Sektor</th>
                            <th>Fra °</th>
                            <th>Til °</th>
                            <th>{`Lengde fra (${unit})`}</th>
                            <th>{`Lengde til (${unit})`}</th>
                            <th>{`Radius (${unit})`}</th>
                            <th>Farge</th>
                            <th>Beskrivelse</th>
                            <th />
                        </tr>
                    </thead>
                    <tbody>
                        {drafts.map((draft, index) => (
                            <tr key={draft.key}>
                                <td className="flex-row small-gap align-center">
                                    <SectorColorIndicator color={draft.color} height="1rem" />
                                    {sectorNumbers[index] ?? "-"}
                                </td>
                                {numberCell(draft, "fromAngle")}
                                {numberCell(draft, "toAngle")}
                                {lengthCell(draft, "fromLineLength")}
                                {lengthCell(draft, "toLineLength")}
                                {lengthCell(draft, "innerRadius")}
                                <td>
                                    <NfsDropdown
                                        options={Object.keys(SectorColor)}
                                        selectedOption={
                                            Object.entries(SectorColor).find(([, v]) => v === draft.color)?.[0] ?? null
                                        }
                                        setSelectedOption={(option) => {
                                            if (option) {
                                                updateDraft(draft.key, {
                                                    color: SectorColor[option as keyof typeof SectorColor],
                                                });
                                            }
                                        }}
                                        placeholder=""
                                        displayValueFunction={(option) => (
                                            <div className="flex-row small-gap center">
                                                <SectorColorIndicator
                                                    color={SectorColor[option as keyof typeof SectorColor]}
                                                    height="1rem"
                                                />
                                                {colorTranslations[option as keyof typeof colorTranslations]}
                                            </div>
                                        )}
                                    />
                                </td>
                                {draft.color === SectorColor.DARK ? (
                                    <td
                                        className="sector-definition-not-applicable"
                                        title="Gjelder ikke mørke sektorer"
                                    >
                                        –
                                    </td>
                                ) : (
                                    <td>
                                        <InputField
                                            inputFieldId={`${draft.key}-description`}
                                            label=""
                                            type="text"
                                            isTextArea
                                            textAreaRows={2}
                                            value={draft.description}
                                            maxLength={MAX_SECTOR_DESCRIPTION_LENGTH}
                                            onChange={(value) => updateDraft(draft.key, { description: value })}
                                        />
                                    </td>
                                )}
                                <td>
                                    <div className="flex-row small-gap">
                                        <NfsButton
                                            onClick={() => insertDraftBelow(index)}
                                            title="Sett inn sektor under"
                                            round
                                            small
                                        >
                                            <AddIcon />
                                        </NfsButton>
                                        <NfsButton
                                            onClick={() =>
                                                setDrafts((current) => current.filter((row) => row.key !== draft.key))
                                            }
                                            title="Slett sektor"
                                            disabled={drafts.length === 1}
                                            round
                                            small
                                        >
                                            <TrashCanIcon />
                                        </NfsButton>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className="sector-definition-findings">
                {tableFindings.map((finding) =>
                    finding.severity === "warning" ? (
                        <div key={finding.message} className="sector-definition-warning">
                            {`Advarsel: ${finding.message}`}
                        </div>
                    ) : (
                        <ErrorMessage key={finding.message} message={finding.message} light />
                    )
                )}
            </div>

            <div className="flex-row small-gap">
                <NfsButton onClick={() => setDrafts((current) => [...current, emptySectorDraft(unit)])}>
                    <AddIcon />
                    Ny sektor
                </NfsButton>
                <NfsButton onClick={close} empty outlined>
                    Avbryt
                </NfsButton>
                <NfsButton onClick={save} disabled={hasBlockingErrors(findings) || isCreating}>
                    {isEditing ? "Lagre" : "Opprett"}
                </NfsButton>
            </div>
        </FloatingWindow>
    );
};

export default SectorDefinitionTable;
